import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  Space,
  Typography,
  Alert,
  List,
  Tag,
  Tooltip,
  Modal,
  message,
  Row,
  Col,
  Divider
} from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import MomentDatePicker from './MomentDatePicker.tsx';
import { validateReviewData, sanitizeTextInput } from '../utils/smartGoalValidation';
import { handleReviewError, handleOfflineError, retryApiCall } from '../utils/smartGoalErrorHandling';

const { TextArea } = Input;
const { Text, Title } = Typography;

const ReviewSchedulingComponent = ({
  smartGoalId,
  goal,
  onReviewUpdate,
  readOnly = false
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [localNextReviewDate, setLocalNextReviewDate] = useState(null);

  const currentDate = moment();
  // Use local state if available, otherwise fall back to goal prop
  const nextReviewDate = localNextReviewDate || (goal?.timeline?.nextReviewDate ? moment(goal.timeline.nextReviewDate) : null);
  const lastReviewDate = goal?.timeline?.lastReviewed ? moment(goal.timeline.lastReviewed) : null;
  const isReviewOverdue = nextReviewDate && currentDate.isAfter(nextReviewDate);
  const daysUntilReview = nextReviewDate ? nextReviewDate.diff(currentDate, 'days') : null;

  // Initialize form with current review date and sync local state
  useEffect(() => {
    if (goal?.timeline?.nextReviewDate) {
      const nextDate = moment(goal.timeline.nextReviewDate);
      form.setFieldsValue({
        nextReviewDate: nextDate,
        reviewNotes: goal.hmReviewNote || ''
      });
      setLocalNextReviewDate(nextDate);
    }
  }, [goal, form]);

  // Handle review completion
  const handleCompleteReview = async () => {
    try {
      setLoading(true);

      // Check network connectivity
      if (handleOfflineError('complete review')) {
        return;
      }

      const values = await form.validateFields();

      // Sanitize and validate review data
      const reviewData = {
        nextReviewDate: values.nextReviewDate.toISOString(),
        reviewNotes: values.reviewNotes ? sanitizeTextInput(values.reviewNotes) : ''
      };

      const validation = validateReviewData(reviewData);
      if (!validation.isValid) {
        message.error(`Review validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      const response = await retryApiCall(
        () => axios.patch(`/smart-goals/${smartGoalId}/review`, reviewData),
        2,
        500
      );

      // Immediately update local state for instant UI feedback
      const newNextReviewDate = moment(values.nextReviewDate);
      setLocalNextReviewDate(newNextReviewDate);
      
      message.success('Review completed and next review scheduled');
      setReviewModalVisible(false);

      if (onReviewUpdate) {
        onReviewUpdate(response.data.data.smartGoal);
      }
    } catch (error) {
      handleReviewError(error, 'complete review');
    } finally {
      setLoading(false);
    }
  };

  // Handle quick reschedule
  const handleQuickReschedule = async (days) => {
    try {
      setLoading(true);
      const newReviewDate = moment().add(days, 'days');

      const response = await axios.patch(`/smart-goals/${smartGoalId}/review`, {
        nextReviewDate: newReviewDate.toISOString(),
        reviewNotes: `Review rescheduled for ${days} days from now`
      });

      // Immediately update local state for instant UI feedback
      setLocalNextReviewDate(newReviewDate);
      
      message.success(`Review rescheduled to ${newReviewDate.format('MMM DD, YYYY')}`);

      if (onReviewUpdate) {
        onReviewUpdate(response.data.data.smartGoal);
      }
    } catch (error) {
      console.error('Error rescheduling review:', error);
      message.error('Failed to reschedule review');
    } finally {
      setLoading(false);
    }
  };

  // Fetch review history
  const fetchReviewHistory = async () => {
    try {
      // This would need a backend endpoint to get review history from timeline events
      const response = await axios.get(`/smart-goals/${smartGoalId}`);
      const timelineEvents = response.data.data.smartGoal.timelineEvents || [];
      
      // Filter for review-related events
      const reviewEvents = timelineEvents.filter(event => 
        event.eventType === 'Modified' && 
        event.description.toLowerCase().includes('review')
      );
      
      setReviewHistory(reviewEvents);
      setHistoryModalVisible(true);
    } catch (error) {
      console.error('Error fetching review history:', error);
      message.error('Failed to fetch review history');
    }
  };

  const getReviewStatus = () => {
    if (!nextReviewDate) {
      return { status: 'warning', text: 'No review scheduled', color: '#fa8c16' };
    }
    
    if (isReviewOverdue) {
      return { status: 'error', text: 'Review overdue', color: '#f5222d' };
    }
    
    if (daysUntilReview <= 7) {
      return { status: 'warning', text: 'Review due soon', color: '#fa8c16' };
    }
    
    return { status: 'success', text: 'Review scheduled', color: '#52c41a' };
  };

  const reviewStatus = getReviewStatus();

  return (
    <Card
      title={
        <Space>
          <CalendarOutlined />
          Review Schedule
          <Tag color={reviewStatus.status}>{reviewStatus.text}</Tag>
        </Space>
      }
      size="small"
      className="review-scheduling-component"
      extra={
        !readOnly && (
          <Space>
            <Tooltip title="View Review History">
              <Button
                size="small"
                icon={<HistoryOutlined />}
                onClick={fetchReviewHistory}
              />
            </Tooltip>
            <Button
              size="small"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setReviewModalVisible(true)}
            >
              Schedule Review
            </Button>
          </Space>
        )
      }
    >
      <Row gutter={16}>
        <Col span={12}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Next Review: </Text>
              {nextReviewDate ? (
                <Text style={{ color: reviewStatus.color }}>
                  {nextReviewDate.format('MMM DD, YYYY')}
                </Text>
              ) : (
                <Text type="secondary">Not scheduled</Text>
              )}
            </div>
            
            {daysUntilReview !== null && (
              <div>
                <Text strong>Days Until Review: </Text>
                <Text style={{ color: reviewStatus.color }}>
                  {daysUntilReview > 0 ? daysUntilReview : `${Math.abs(daysUntilReview)} days overdue`}
                </Text>
              </div>
            )}
            
            {lastReviewDate && (
              <div>
                <Text strong>Last Reviewed: </Text>
                <Text type="secondary">
                  {lastReviewDate.format('MMM DD, YYYY')}
                </Text>
              </div>
            )}
          </Space>
        </Col>
        
        <Col span={12}>
          {!readOnly && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Quick Actions:</Text>
              <Space wrap>
                <Button
                  size="small"
                  onClick={() => handleQuickReschedule(7)}
                  loading={loading}
                >
                  +1 Week
                </Button>
                <Button
                  size="small"
                  onClick={() => handleQuickReschedule(14)}
                  loading={loading}
                >
                  +2 Weeks
                </Button>
                <Button
                  size="small"
                  onClick={() => handleQuickReschedule(30)}
                  loading={loading}
                >
                  +1 Month
                </Button>
                <Button
                  size="small"
                  onClick={() => handleQuickReschedule(90)}
                  loading={loading}
                >
                  +3 Months
                </Button>
              </Space>
            </Space>
          )}
        </Col>
      </Row>

      {isReviewOverdue && (
        <Alert
          message="Review Overdue"
          description={`This goal's review was due ${Math.abs(daysUntilReview)} days ago. Please complete the review and schedule the next one.`}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          action={
            !readOnly && (
              <Button
                size="small"
                type="primary"
                onClick={() => setReviewModalVisible(true)}
              >
                Complete Review
              </Button>
            )
          }
        />
      )}

      {/* Review Modal */}
      <Modal
        title="Complete Review & Schedule Next"
        open={reviewModalVisible}
        onCancel={() => setReviewModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setReviewModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={handleCompleteReview}>
            Complete Review
          </Button>
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Alert
            message="Review Completion"
            description="Complete the current review and schedule the next review date."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="nextReviewDate"
            label="Next Review Date"
            rules={[{ required: true, message: 'Please select next review date' }]}
          >
            <MomentDatePicker
              style={{ width: '100%' }}
              disabledDate={(current) => current && current < moment().endOf('day')}
            />
          </Form.Item>

          <Form.Item
            name="reviewNotes"
            label="Review Notes"
            extra="Document findings, progress, and any adjustments made"
          >
            <TextArea
              rows={4}
              placeholder="Enter review notes..."
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Divider />

          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Quick Schedule Options:</Text>
            <Space wrap>
              <Button
                size="small"
                onClick={() => form.setFieldsValue({ nextReviewDate: moment().add(1, 'week') })}
              >
                1 Week
              </Button>
              <Button
                size="small"
                onClick={() => form.setFieldsValue({ nextReviewDate: moment().add(2, 'weeks') })}
              >
                2 Weeks
              </Button>
              <Button
                size="small"
                onClick={() => form.setFieldsValue({ nextReviewDate: moment().add(1, 'month') })}
              >
                1 Month
              </Button>
              <Button
                size="small"
                onClick={() => form.setFieldsValue({ nextReviewDate: moment().add(3, 'months') })}
              >
                3 Months
              </Button>
            </Space>
          </Space>
        </Form>
      </Modal>

      {/* Review History Modal */}
      <Modal
        title="Review History"
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {reviewHistory.length > 0 ? (
          <List
            size="small"
            dataSource={reviewHistory}
            renderItem={(event) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<CalendarOutlined />}
                  title={
                    <Space>
                      <Text strong>{event.eventType}</Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {moment(event.eventDate).format('MMM DD, YYYY HH:mm')}
                      </Text>
                    </Space>
                  }
                  description={event.description}
                />
              </List.Item>
            )}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
            <HistoryOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
            <div>No review history available</div>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default ReviewSchedulingComponent;
